'use client'

import { useState, useEffect, useMemo } from 'react'
import {
  Loader2,
  Search,
  Clock,
  Heart,
  X,
  ChevronDown,
} from 'lucide-react'
import Link from 'next/link'
import {
  INTENSITY_CONFIG,
  INTENTION_CONFIG,
  SEARCH_SYNONYMS,
  EMOTION_TOPICS,
  LIFE_CONTEXT_TOPICS,
  PRIMARY_EXPERIENCES,
  TOPIC_TO_PRIMARY_EXPERIENCE,
} from '@repo/constants'

interface Resource {
  id: string
  slug: string | null
  title: string
  clientTitle: string | null
  description: string | null
  resourceType: string
  isPublic: boolean
  createdAt: string
  updatedAt: string
  topics: string[] | null
  primaryExperiences: string[] | null
  modality: string[] | null
  intensity: 'GENTLE' | 'MODERATE' | 'DEEPER' | null
  estimatedMinutes: number | null
  intention: string[] | null
  format: 'READING' | 'GUIDED_EXERCISE' | 'WRITING_PROMPT' | 'AUDIO_VIDEO' | null
}

// Use shared constants for filters
const EMOTION_FILTERS = EMOTION_TOPICS
const CONTEXT_FILTERS = LIFE_CONTEXT_TOPICS

// Helper to format primary experience labels (e.g., 'overthinking-rumination' -> 'Overthinking & Rumination')
const formatPrimaryExperience = (exp: string) => {
  return exp
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' & ')
    .replace(' & ', ' / ') // Use / for better readability
}

export default function ResourcesPage() {
  const [resources, setResources] = useState<Resource[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedTopic, setSelectedTopic] = useState<string | null>(null)
  const [selectedIntensity, setSelectedIntensity] = useState<string | null>(null)
  const [selectedIntention, setSelectedIntention] = useState<string | null>(null)
  const [selectedPrimaryExperience, setSelectedPrimaryExperience] = useState<string | null>(null)
  const [expandedSection, setExpandedSection] = useState<'emotions' | 'contexts' | 'experiences' | null>(null)

  useEffect(() => {
    const fetchResources = async () => {
      setIsLoading(true)
      setError(null)
      try {
        const response = await fetch('/api/resources')
        const data = await response.json()

        if (!data.success) {
          setError(data.error || 'Failed to load resources')
          return
        }

        setResources(data.resources)
      } catch (err) {
        console.error('[Resources] Error:', err)
        setError('Failed to load resources')
      } finally {
        setIsLoading(false)
      }
    }

    fetchResources()
  }, [])

  // Use all emotions and contexts from constants (not filtered by resources)
  const emotionTopics: string[] = [...EMOTION_FILTERS]
  const contextTopics: string[] = [...CONTEXT_FILTERS]

  const filteredResources = useMemo(() => {
    return resources.filter((resource) => {
      // Topic filter - also checks mapped primary experiences
      if (selectedTopic) {
        const hasTopicMatch = resource.topics?.includes(selectedTopic)
        // Also check if resource has any mapped primary experiences for this topic
        const mappedExperiences = TOPIC_TO_PRIMARY_EXPERIENCE[selectedTopic] || []
        const hasMappedExpMatch = mappedExperiences.some(exp =>
          resource.primaryExperiences?.includes(exp)
        )
        // Match if topic matches OR any mapped primary experience matches
        if (!hasTopicMatch && !hasMappedExpMatch) return false
      }

      // Primary experience filter
      if (selectedPrimaryExperience) {
        const hasExpMatch = resource.primaryExperiences?.includes(selectedPrimaryExperience)
        if (!hasExpMatch) return false
      }

      // Intensity filter
      if (selectedIntensity && resource.intensity !== selectedIntensity) {
        return false
      }

      // Intention filter
      if (selectedIntention) {
        const hasIntentionMatch = resource.intention?.includes(selectedIntention)
        if (!hasIntentionMatch) return false
      }

      // Search with synonym support
      if (searchQuery) {
        const query = searchQuery.toLowerCase().trim()
        const displayTitle = resource.clientTitle || resource.title

        // Direct matches
        const matchesTitle = displayTitle.toLowerCase().includes(query)
        const matchesDesc = resource.description?.toLowerCase().includes(query)
        const matchesTopics = resource.topics?.some(t => t.toLowerCase().includes(query))
        const matchesPrimaryExp = resource.primaryExperiences?.some(p => p.toLowerCase().includes(query))

        // Synonym matching - check if query maps to topics, primaryExperiences, or intentions
        let matchesSynonym = false
        const synonymTargets = SEARCH_SYNONYMS[query]
        if (synonymTargets) {
          // Check if any synonym targets match resource topics
          matchesSynonym = synonymTargets.some(target =>
            resource.topics?.some(t => t.toLowerCase() === target.toLowerCase()) ||
            resource.primaryExperiences?.some(p => p.toLowerCase() === target.toLowerCase()) ||
            resource.intention?.some(i => i.toLowerCase() === target.toLowerCase())
          )
        }

        // Also check for partial synonym matches (e.g., "feel stressed" should match "stressed")
        if (!matchesSynonym) {
          for (const [term, targets] of Object.entries(SEARCH_SYNONYMS)) {
            if (query.includes(term)) {
              matchesSynonym = targets.some(target =>
                resource.topics?.some(t => t.toLowerCase() === target.toLowerCase()) ||
                resource.primaryExperiences?.some(p => p.toLowerCase() === target.toLowerCase()) ||
                resource.intention?.some(i => i.toLowerCase() === target.toLowerCase())
              )
              if (matchesSynonym) break
            }
          }
        }

        if (!matchesTitle && !matchesDesc && !matchesTopics && !matchesPrimaryExp && !matchesSynonym) {
          return false
        }
      }

      return true
    })
  }, [resources, selectedTopic, selectedPrimaryExperience, selectedIntensity, selectedIntention, searchQuery])

  const clearFilters = () => {
    setSelectedTopic(null)
    setSelectedPrimaryExperience(null)
    setSelectedIntensity(null)
    setSelectedIntention(null)
    setSearchQuery('')
    setExpandedSection(null)
  }

  const hasActiveFilters = selectedTopic || selectedPrimaryExperience || selectedIntensity || selectedIntention || searchQuery

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <Loader2 className="h-8 w-8 text-slate-400 animate-spin mx-auto" />
            <p className="mt-3 text-sm text-slate-500">Loading resources...</p>
          </div>
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        <div className="flex items-center justify-center min-h-screen px-4">
          <div className="text-center max-w-sm">
            <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mx-auto">
              <X className="h-6 w-6 text-red-500" />
            </div>
            <h2 className="mt-4 text-lg font-semibold text-slate-900">Something went wrong</h2>
            <p className="mt-2 text-sm text-slate-500">We couldn&apos;t load the resources. Please try again.</p>
            <button
              onClick={() => window.location.reload()}
              className="mt-4 px-4 py-2 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800 transition-colors"
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-slate-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-400 to-emerald-500 flex items-center justify-center">
              <Heart className="h-5 w-5 text-white" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900">
              Resources
            </h1>
          </div>
          <p className="text-slate-600 max-w-xl">
            Tools and exercises to support you between sessions
          </p>
        </div>
      </header>

      {/* Search & Quick Filters */}
      <div className="sticky top-0 z-40 bg-white/95 backdrop-blur-sm border-b border-slate-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search resources..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-10 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-slate-200 rounded-full transition-colors"
              >
                <X className="h-4 w-4 text-slate-400" />
              </button>
            )}
          </div>

          {/* Filter Buttons */}
          <div className="mt-3 flex flex-wrap gap-2">
            {/* Feelings Button */}
            <button
              onClick={() => setExpandedSection(expandedSection === 'emotions' ? null : 'emotions')}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                expandedSection === 'emotions' || (selectedTopic && emotionTopics.includes(selectedTopic))
                  ? 'bg-rose-500 text-white'
                  : 'bg-rose-50 text-rose-700 hover:bg-rose-100 border border-rose-200'
              }`}
            >
              <Heart className="h-3.5 w-3.5" />
              I'm feeling...
              <ChevronDown className={`h-3.5 w-3.5 transition-transform ${expandedSection === 'emotions' ? 'rotate-180' : ''}`} />
            </button>

            {/* Life Areas Button */}
            <button
              onClick={() => setExpandedSection(expandedSection === 'contexts' ? null : 'contexts')}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                expandedSection === 'contexts' || (selectedTopic && contextTopics.includes(selectedTopic))
                  ? 'bg-indigo-500 text-white'
                  : 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100 border border-indigo-200'
              }`}
            >
              Related to...
              <ChevronDown className={`h-3.5 w-3.5 transition-transform ${expandedSection === 'contexts' ? 'rotate-180' : ''}`} />
            </button>

            {/* Primary Experiences Button */}
            <button
              onClick={() => setExpandedSection(expandedSection === 'experiences' ? null : 'experiences')}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                expandedSection === 'experiences' || selectedPrimaryExperience
                  ? 'bg-amber-500 text-white'
                  : 'bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200'
              }`}
            >
              I'm struggling with...
              <ChevronDown className={`h-3.5 w-3.5 transition-transform ${expandedSection === 'experiences' ? 'rotate-180' : ''}`} />
            </button>

            {/* Show selected topic as a chip */}
            {selectedTopic && (
              <button
                onClick={() => setSelectedTopic(null)}
                className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium ${
                  emotionTopics.includes(selectedTopic)
                    ? 'bg-rose-100 text-rose-700'
                    : 'bg-indigo-100 text-indigo-700'
                }`}
              >
                {selectedTopic}
                <X className="h-3.5 w-3.5" />
              </button>
            )}

            {/* Show selected primary experience as a chip */}
            {selectedPrimaryExperience && (
              <button
                onClick={() => setSelectedPrimaryExperience(null)}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium bg-amber-100 text-amber-700"
              >
                {formatPrimaryExperience(selectedPrimaryExperience)}
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>

          {/* Expanded Emotions */}
          {expandedSection === 'emotions' && (
            <div className="mt-3 p-4 bg-rose-50/50 rounded-xl border border-rose-100">
              <p className="text-xs font-medium text-rose-600 uppercase tracking-wide mb-3">What are you feeling?</p>
              <div className="flex flex-wrap gap-2">
                {emotionTopics.map((emotion) => (
                  <button
                    key={emotion}
                    onClick={() => {
                      setSelectedTopic(selectedTopic === emotion ? null : emotion)
                      setExpandedSection(null)
                    }}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                      selectedTopic === emotion
                        ? 'bg-rose-500 text-white'
                        : 'bg-white text-rose-700 border border-rose-200 hover:border-rose-400 hover:bg-rose-50'
                    }`}
                  >
                    {emotion}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Expanded Contexts */}
          {expandedSection === 'contexts' && (
            <div className="mt-3 p-4 bg-indigo-50/50 rounded-xl border border-indigo-100">
              <p className="text-xs font-medium text-indigo-600 uppercase tracking-wide mb-3">What area of life?</p>
              <div className="flex flex-wrap gap-2">
                {contextTopics.map((context) => (
                  <button
                    key={context}
                    onClick={() => {
                      setSelectedTopic(selectedTopic === context ? null : context)
                      setExpandedSection(null)
                    }}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                      selectedTopic === context
                        ? 'bg-indigo-500 text-white'
                        : 'bg-white text-indigo-700 border border-indigo-200 hover:border-indigo-400 hover:bg-indigo-50'
                    }`}
                  >
                    {context}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Expanded Primary Experiences */}
          {expandedSection === 'experiences' && (
            <div className="mt-3 p-4 bg-amber-50/50 rounded-xl border border-amber-100">
              <p className="text-xs font-medium text-amber-600 uppercase tracking-wide mb-3">What pattern are you noticing?</p>
              <div className="flex flex-wrap gap-2">
                {PRIMARY_EXPERIENCES.map((exp) => (
                  <button
                    key={exp}
                    onClick={() => {
                      setSelectedPrimaryExperience(selectedPrimaryExperience === exp ? null : exp)
                      setExpandedSection(null)
                    }}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                      selectedPrimaryExperience === exp
                        ? 'bg-amber-500 text-white'
                        : 'bg-white text-amber-700 border border-amber-200 hover:border-amber-400 hover:bg-amber-50'
                    }`}
                  >
                    {formatPrimaryExperience(exp)}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Intention Filter */}
          <div className="mt-3 flex items-center gap-2">
            <span className="text-xs text-slate-500">I want to:</span>
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(INTENTION_CONFIG).map(([key, config]) => (
                <button
                  key={key}
                  onClick={() => setSelectedIntention(selectedIntention === key ? null : key)}
                  className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border transition-all ${
                    selectedIntention === key
                      ? 'bg-teal-500 text-white border-teal-500'
                      : 'bg-white text-slate-600 border-slate-200 hover:border-teal-300 hover:text-teal-600'
                  }`}
                  title={config.description}
                >
                  <span>{config.emoji}</span>
                  <span>{config.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Intensity Filter */}
          <div className="mt-3 flex items-center gap-2">
            <span className="text-xs text-slate-500">Intensity:</span>
            <div className="flex gap-1.5">
              {Object.entries(INTENSITY_CONFIG).map(([key, config]) => (
                <button
                  key={key}
                  onClick={() => setSelectedIntensity(selectedIntensity === key ? null : key)}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-all ${
                    selectedIntensity === key
                      ? config.color + ' border-current'
                      : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300'
                  }`}
                >
                  {config.label}
                </button>
              ))}
            </div>
          </div>

          {/* Active filters indicator */}
          {hasActiveFilters && (
            <div className="mt-3 flex items-center justify-between">
              <p className="text-sm text-slate-500">
                {filteredResources.length} resource{filteredResources.length !== 1 ? 's' : ''} found
              </p>
              <button
                onClick={clearFilters}
                className="text-sm text-teal-600 hover:text-teal-700 font-medium transition-colors"
              >
                Clear filters
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
        {filteredResources.length === 0 ? (
          <div className="text-center py-16">
            <Search className="h-10 w-10 text-slate-300 mx-auto" />
            <h3 className="mt-4 text-lg font-medium text-slate-900">No resources found</h3>
            <p className="mt-1 text-sm text-slate-500">
              {searchQuery ? `No results for "${searchQuery}"` : 'Try adjusting your filters'}
            </p>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="mt-4 text-sm font-medium text-teal-600 hover:text-teal-700 transition-colors"
              >
                Clear filters
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {filteredResources.map((resource) => {
              const intensityConfig = resource.intensity ? INTENSITY_CONFIG[resource.intensity] : null

              return (
                <Link
                  key={resource.id}
                  href={`/resources/${resource.slug || resource.id}`}
                  className="block bg-white rounded-2xl border border-slate-200 p-5 hover:border-teal-300 hover:shadow-md transition-all duration-200 group"
                >
                  <h3 className="font-semibold text-slate-900 group-hover:text-teal-700 transition-colors">
                    {resource.clientTitle || resource.title}
                  </h3>

                  {resource.description && (
                    <p className="mt-1.5 text-sm text-slate-500 line-clamp-2">
                      {resource.description}
                    </p>
                  )}

                  {/* Meta info */}
                  {(resource.estimatedMinutes || intensityConfig) && (
                    <div className="mt-3 flex items-center gap-3 text-xs text-slate-400">
                      {resource.estimatedMinutes && (
                        <span className="inline-flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {resource.estimatedMinutes} min
                        </span>
                      )}
                      {intensityConfig && (
                        <span>{intensityConfig.label}</span>
                      )}
                    </div>
                  )}
                </Link>
              )
            })}
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="mt-auto py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <img src="/alli_logo.png" alt="Alli" className="h-5 w-auto mx-auto opacity-30" />
          <p className="mt-2 text-xs text-slate-400">Resources shared by your therapist</p>
        </div>
      </footer>
    </div>
  )
}
